import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { isAPIError } from '@/store/utils';
import type { APIError } from '@/types/api.types';
import type { LoginDTO, LoginResult, LoginReturn } from '@/types/DTOs';
import { setUkek } from '@/store/slices/auth/authSlice';
import { getUserIdFromToken } from '@/store/utils/jwtDecoder';
import { setUkekInStorage } from '@/utils/sessionStorage';

const HTTP_UNAUTHORIZED = 401;
/** Backend discriminator from login-init when an admin has forced a credential reset. */
const CREDENTIALS_RESET_REQUIRED_CODE = 'CREDENTIALS_RESET_REQUIRED';

/**
 * True when login-init blocked the sign-in because the account is under an
 * admin-forced credential reset. Prefers the machine-readable `code`; falls back
 * to matching the 401 message until the backend discriminator is deployed.
 */
const isCredentialsResetRequiredError = (error: APIError): boolean => {
  if (error.code === CREDENTIALS_RESET_REQUIRED_CODE) return true;
  return (
    error.status === HTTP_UNAUTHORIZED &&
    typeof error.message === 'string' &&
    /password reset required/i.test(error.message)
  );
};

const mapCredentialVerificationResult = (result: LoginResult): LoginReturn => ({
  twoFactorToken: result.twoFactorToken,
});

export const credentialVerificationThunk = createAppAsyncThunk<LoginReturn, LoginDTO>(
  'auth/login',
  async (
    credentials,
    {
      dispatch,
      extra: {
        authService,
        pocServices: { cryptoService },
      },
      rejectWithValue,
    }
  ) => {
    const normalizedCredentials = { ...credentials, username: credentials.username.toLowerCase() };
    try {
      const result = await authService.login(normalizedCredentials);
      const mappedResult = mapCredentialVerificationResult(result);
      const userId = getUserIdFromToken(mappedResult.twoFactorToken);
      const ukek = await cryptoService.deriveKey(
        normalizedCredentials.password,
        normalizedCredentials.username
      );

      dispatch(setUkek(ukek));
      if (userId !== null) {
        setUkekInStorage(userId, ukek);
      }

      return mappedResult;
    } catch (error) {
      if (isAPIError(error)) {
        if (isCredentialsResetRequiredError(error)) {
          return rejectWithValue({
            credentialsResetRequired: true,
            emailAddress: normalizedCredentials.username,
          });
        }
        if (error.fieldErrors != null) {
          return rejectWithValue({ fieldErrors: error.fieldErrors });
        }
      }
      throw error;
    }
  }
);
